import { Stack, TextField, Typography } from '@mui/material';
import { NotificationPolicyTypeEnum } from '@repo/api-gw-sdk';

import type { StepProps } from '@/components/wizard/StepProps';

import { NotificationPolicyTypes } from './data';
import type { UpsertNotificationPolicyState } from './upsertNotificationPolicyFlow';

import { Link } from '../shared/link';
import { StepContainer } from '../wizard/StepContainer';
import { RenameEntityName } from '../wizard/renameEntityName';

export const NotificationPolicyCredentials = (
  props: StepProps<UpsertNotificationPolicyState>
) => {
  const next = async () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  const validateParams = () => {
    if (props.wizardState.type === NotificationPolicyTypeEnum.Email) {
      return !!props.wizardState.config?.email?.email;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Sns) {
      return !!props.wizardState.config?.sns?.topicArn;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Splunk) {
      return !!props.wizardState.config?.splunk?.token;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Slack) {
      return (
        !!props.wizardState.config?.slack?.channel &&
        !!props.wizardState.config?.slack?.token
      );
    }

    return false;
  };

  return (
    <StepContainer
      hideStepper
      canGoNext={validateParams}
      nextButtonText={'Next'}
      onNextClick={next}
      navigationComponent={
        <RenameEntityName
          label='Notification policy name'
          name={props.wizardState.name}
          onChange={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <Stack
        sx={{ height: '100%' }}
        alignItems='center'
        justifyContent='center'
        gap='24px'
      >
        <Typography
          variant='h3'
          sx={{ textAlign: 'center', marginBottom: '16px' }}
        >
          {NotificationPolicyTypes[props.wizardState.type]?.parametersTitle}
        </Typography>

        {props.wizardState.type === NotificationPolicyTypeEnum.Email && (
          <Stack className='w-[582px]' gap='8px'>
            <Typography>Email</Typography>
            <TextField
              value={props.wizardState.config?.email?.email || ''}
              placeholder='Example: yourname@example.com'
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    email: {
                      email: event.target.value,
                    },
                  },
                }))
              }
            />
          </Stack>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Sns && (
          <Stack className='w-[582px]' gap='8px'>
            <Typography>Topic ARN</Typography>
            <TextField
              value={props.wizardState.config?.sns?.topicArn || ''}
              placeholder='Example: arn:aws:sns:us-east-1:123456789012:eon-notifications'
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    sns: {
                      topicArn: event.target.value,
                    },
                  },
                }))
              }
            />
            <Typography
              fontSize={'12px'}
              variant='subtitle2'
            >{`Copy this from the SNS topic details page.`}</Typography>
          </Stack>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Splunk && (
          <Stack className='w-[582px]' gap='8px'>
            <Typography>Token</Typography>
            <TextField
              value={props.wizardState.config?.splunk?.token || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    splunk: {
                      token: event.target.value,
                    },
                  },
                }))
              }
            />
          </Stack>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Slack && (
          <>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Channel ID</Typography>
              <TextField
                value={props.wizardState.config?.slack?.channel || ''}
                placeholder='Example: D07GNGUCLUW'
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      slack: {
                        token: state.config?.slack?.token,
                        channel: event.target.value,
                      },
                    },
                  }))
                }
              />
              <Typography
                fontSize={'12px'}
                variant='subtitle2'
              >{`Find this in Slack's channel management dialog by clicking the channel's title and scrolling to the bottom of the tab.`}</Typography>
            </Stack>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Bot user OAuth token</Typography>
              <TextField
                value={props.wizardState.config?.slack?.token || ''}
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      slack: {
                        channel: state.config?.slack?.channel,
                        token: event.target.value,
                      },
                    },
                  }))
                }
              />
              <Typography fontSize={'12px'} variant='subtitle2'>
                Copy this from the OAuth & Permissions page in your&nbsp;
                <Link target='_blank' href='https://api.slack.com/apps'>
                  Slack app&apos;s
                </Link>
                &nbsp;configuration.
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </StepContainer>
  );
};
